import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import ProcessingButton from '../../ProcessingButton';
import styles from '../console.module.scss';

import { FieldNumber } from '@/components/ui/kit/Field';
import ProgressBar from '@/components/ui/ProgressBar';
import useParamCommand from '@/hooks/command/mavlink/useParamCommand';
import useTelemetryData from '@/hooks/useTelemetryData';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Console = ({ robot }) => {
  const robotParams = useSelector((state) => state.robotParams[robot.id]);
  const [params, setParams] = useState({
    FS_GCS_ENABL: null,
    FS_SHORT_ACTN: null,
    FS_SHORT_TIMEOUT: null,
    FS_LONG_ACTN: null,
    FS_LONG_TIMEOUT: null,
  });

  const { getAddress } = useTelemetryData();
  const paramCommand = useParamCommand();

  // 수신된 파라미터 갯수
  const isLoadedCount = useMemo(() => {
    return Object.values(params).filter((value) => value !== null).length;
  }, [params]);

  // 파라미터 수신 중 여부
  const isLoading = useMemo(() => {
    return isLoadedCount < Object.keys(params).length;
  }, [isLoadedCount]);

  useMountEffect(() => {
    const address = getAddress(robot.id);
    const paramsGroup = Object.keys(params).map((key) => [...address, key, -1]);

    paramCommand.read(robot, paramsGroup);
  });

  useUpdateEffect(() => {
    const { lastParamId } = robotParams;
    if (!Object.keys(params).includes(lastParamId)) return;

    setParams((prev) => ({
      ...prev,
      [lastParamId]: {
        value: robotParams.params[lastParamId].value,
        type: robotParams.params[lastParamId].type,
      },
    }));
  }, [robotParams]);

  const handleChange = (e) => {
    const nextParams = { ...params };
    nextParams[e.target.name].value = e.target.value;

    setParams(nextParams);
  };

  const handleBlur = (e) => {
    const nextParams = { ...params };
    nextParams[e.target.name].value = Number(e.target.value).toFixed(1);

    setParams(nextParams);
  };

  const doSave = async (...prefixes) => {
    const paramSet = Object.entries(params)
      .filter(([key]) => prefixes.flat().some((prefix) => key.startsWith(prefix)))
      .map(([id, { value, type }]) => ({ id, value, type }));

    for (const param of paramSet) {
      try {
        await executeMavLinkCommand(robot, paramCommand.set(param.id, Number(param.value), param.type));
      } catch (error) {
        return false;
      }
    }
    return true;
  };

  if (isLoading) {
    return (
      <div className={cx('loading')}>
        <ProgressBar current={isLoadedCount} total={Object.keys(params).length} />
      </div>
    );
  }

  return (
    <div className={cx('container')}>
      <div className={cx('rows')}>
        <div className={cx('row')}>
          <div className={cx('label')}>GCS FailSafe</div>
          <div className={cx('radioGroup')}>
            <label>
              <input
                type="radio"
                name="FS_GCS_ENABL"
                value={0}
                defaultChecked={params['FS_GCS_ENABL'].value === 0}
                onChange={handleChange}
              />
              Disabled
            </label>
            <label>
              <input
                type="radio"
                name="FS_GCS_ENABL"
                value={1}
                defaultChecked={params['FS_GCS_ENABL'].value === 1}
                onChange={handleChange}
              />
              RTL
            </label>
          </div>
        </div>
        <div className={cx('row')}>
          <div className={cx('label')}>Short Action</div>
          <div className={cx('radioGroup')}>
            <label>
              <input
                type="radio"
                name="FS_SHORT_ACTN"
                value={0}
                defaultChecked={params['FS_SHORT_ACTN'].value === 0}
                onChange={handleChange}
              />
              Circle or Continue (in AUTO | GUIDED | LOITER)
            </label>
            <label>
              <input
                type="radio"
                name="FS_SHORT_ACTN"
                value={1}
                defaultChecked={params['FS_SHORT_ACTN'].value === 1}
                onChange={handleChange}
              />
              Circle
            </label>
          </div>
        </div>
        <div className={cx(['row', 'center'])}>
          <div className={cx('label')}>Short Timer</div>
          <div className={cx('fieldWrapper')}>
            <FieldNumber
              name="FS_SHORT_TIMEOUT"
              value={params['FS_SHORT_TIMEOUT'].value}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <span className={cx('unit')}>sec</span>
          </div>
        </div>
        <div className={cx('row')}>
          <div className={cx('label')}>Long Action</div>
          <div className={cx('radioGroup')}>
            <label>
              <input
                type="radio"
                name="FS_LONG_ACTN"
                value={0}
                defaultChecked={params['FS_LONG_ACTN'].value === 0}
                onChange={handleChange}
              />
              RTL or Continue (in AUTO | GUIDED | LOITER)
            </label>
            <label>
              <input
                type="radio"
                name="FS_LONG_ACTN"
                value={1}
                defaultChecked={params['FS_LONG_ACTN'].value === 1}
                onChange={handleChange}
              />
              RTL
            </label>
          </div>
        </div>
        <div className={cx(['row', 'center'])}>
          <div className={cx('label')}>Long Timer</div>
          <div className={cx('fieldWrapper')}>
            <FieldNumber
              name="FS_LONG_TIMEOUT"
              value={params['FS_LONG_TIMEOUT'].value}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <span className={cx('unit')}>sec</span>
          </div>
        </div>
      </div>
      <ProcessingButton text="Save" doProcess={() => doSave('FS_GCS', 'FS_SHORT', 'FS_LONG')} />
    </div>
  );
};

export default Console;
